import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import {
  ComponentsService,
  ResolveComponentFamiliesRequestSchema,
} from '../gen/components/v1/components_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

/**
 * Resolves deployed service names to their Backstage-catalog product family
 * ("baic" | "mai" | "csai" | "a8" | "infra"), tolerating tenant-qualified or
 * renamed forms a release manifest's service names take relative to the
 * catalog's canonical name (see internal/management/backstage's
 * CatalogCache.resolve). Only names that resolved to a non-empty family are
 * present in the response's familyByServiceName map - treat a missing key as
 * unresolved.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param serviceNames - deployed service names to resolve
 * @param enabled - pass false to skip the query
 */
export function useResolveComponentFamilies(
  token?: string | null,
  serviceNames?: string[],
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['component-families', ...(serviceNames ?? []).slice().sort()],
    queryFn: async () => {
      const client = createClient(ComponentsService, transport(token));
      const request = create(ResolveComponentFamiliesRequestSchema, {
        serviceNames: serviceNames ?? [],
      });
      return await client.resolveComponentFamilies(request);
    },
    enabled: !!token && !!serviceNames?.length && enabled,
    staleTime: 5 * 60 * 1000,
  });
}
